<div class="container mx-auto px-4 pb-4 pt-2 md:pb-8 md:pt-6">
  @if (loading) {
    <app-loading-spinner message="Loading article..." />
  }
  @if (error) {
    <app-error-message
      title="Error"
      [message]="error"
      [showRetryButton]="true"
      retryButtonText="View All News"
      buttonIcon="lucideArrowLeft"
      (retry)="onNavigateToNews()"
    />
  }
  @if (article && !loading) {
    <article class="mx-auto max-w-2xl">
      <div class="mb-4 flex items-center justify-between -ml-3">
        <a
          hlmBtn
          [size]="'sm'"
          [variant]="'link'"
          routerLink="/news"
          class="text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <ng-icon hlm [size]="'xs'" [name]="'lucideArrowLeft'" class="mr-2" />
          View All News
        </a>
      </div>

      <div
        class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
      >
        <div class="p-6 md:p-8">
          <div class="mb-6 flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <div class="flex items-center">
                <lib-avatar [type]="avatarType" [name]="slug()" [colors]="avatarColors" />
                <div class="ml-3">
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {{ 'Summerry Compiler v0' }}
                  </p>
                  <div class="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <time [dateTime]="article.updatedAt | date: 'yyyy-MM-dd'">
                      {{ article.updatedAt | date: 'MMMM d, y, HH:mm' }}
                    </time>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex space-x-2">
              <!-- Read with AI button -->
              <button
                hlmBtn
                [variant]="'outline'"
                [attr.aria-label]="
                  voiceState === 'playing'
                    ? 'Pause reading'
                    : voiceState === 'paused'
                      ? 'Resume reading'
                      : 'Listen to article'
                "
                (click)="toggleSpeech()"
                class="hidden md:inline-flex"
              >
                <ng-icon
                  hlm
                  [size]="'sm'"
                  class="mr-2"
                  [name]="
                    voiceState === 'playing'
                      ? 'lucidePause'
                      : voiceState === 'paused'
                        ? 'lucidePlay'
                        : 'lucideHeadphones'
                  "
                />
                {{ voiceState === 'playing' ? 'Pause' : voiceState === 'paused' ? 'Resume' : 'Listen' }}
              </button>

              <!-- Mobile icon-only version -->
              <button
                hlmBtn
                [size]="'icon'"
                [variant]="'outline'"
                [attr.aria-label]="
                  voiceState === 'playing'
                    ? 'Pause reading'
                    : voiceState === 'paused'
                      ? 'Resume reading'
                      : 'Listen to article'
                "
                (click)="toggleSpeech()"
                class="md:hidden"
              >
                <ng-icon
                  hlm
                  [size]="'sm'"
                  [name]="
                    voiceState === 'playing'
                      ? 'lucidePause'
                      : voiceState === 'paused'
                        ? 'lucidePlay'
                        : 'lucideHeadphones'
                  "
                />
              </button>

              <!-- Share button -->
              <button hlmBtn [variant]="'outline'" (click)="shareArticle()" class="hidden md:inline-flex">
                <ng-icon hlm [size]="'sm'" class="mr-2" name="lucideShare2" />
                Share
              </button>
              <button hlmBtn [size]="'icon'" [variant]="'outline'" (click)="shareArticle()" class="md:hidden">
                <ng-icon hlm [size]="'sm'" name="lucideShare2" />
              </button>
            </div>
          </div>

          <div class="aspect-video w-full overflow-hidden rounded-lg mb-6">
            <div
              class="h-full w-full flex items-center justify-center"
              [class]="selectedBgClassLight + ' dark:' + selectedBgClassDark"
            >
              <span class="text-5xl md:text-7xl" role="img" [attr.aria-label]="article.image.text">
                {{ article.image.text }}
              </span>
            </div>
          </div>

          <h1 class="mb-6 text-xl font-bold leading-tight text-gray-900 dark:text-gray-100 md:text-3xl">
            {{ article.title }}
          </h1>

          <div class="prose max-w-none dark:prose-invert">
            <p class="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">{{ article.summary }}</p>

            <!-- @if (article.content) {
              <div class="mt-8">
                <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100">Details</h2>
                <p class="text-base leading-relaxed text-gray-700 dark:text-gray-300">{{ article.content }}</p>
              </div>
            } -->
          </div>

          <div class="mt-8 border-t border-gray-200 pt-6 dark:border-gray-700">
            <div class="flex flex-wrap items-center gap-4">
              @for (source of article.sources; track source.url) {
                <a hlmBtn [variant]="'outline'" [href]="source.url" target="_blank" rel="noopener noreferrer">
                  <img
                    [src]="'https://cdn.brandfetch.io/' + (source.url | extractDomain) + '/?c=1idfLKJcOnJ_r6mxmuz'"
                    [alt]="source.name"
                    class="mr-2 h-5 w-5 rounded-full"
                  />
                  <span>{{ source.name }}</span>
                  <ng-icon hlm [size]="'sm'" class="ml-2" name="lucideExternalLink" />
                </a>
              }
            </div>
          </div>

          <div class="md:hidden mt-8 border-t border-gray-200 pt-6 dark:border-gray-700">
            <button hlmBtn [variant]="'outline'" (click)="shareArticle()" class="w-full">
              <ng-icon hlm [size]="'sm'" class="mr-2" name="lucideShare2" />
              Share
            </button>
          </div>
        </div>
      </div>

      <!-- Previous/Next Article Navigation -->
      @if (nextArticle || prevArticle) {
        <nav class="mt-8" aria-label="Article navigation">
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            @if (nextArticle) {
              <a
                [routerLink]="['/news', nextArticle.slug]"
                rel="next"
                class="group flex flex-col space-y-2 rounded-lg border border-gray-200 bg-white p-4 transition-all hover:-translate-y-1 hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
                [class.sm:col-span-2]="!prevArticle"
                [attr.aria-label]="'Read next article: ' + nextArticle.title"
              >
                <div class="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <ng-icon hlm [size]="'sm'" name="lucideArrowLeft" class="mr-1.5" />
                  <span>Next Article</span>
                </div>
                <h3 class="line-clamp-2 text-base font-medium text-gray-900 dark:text-gray-100">
                  {{ nextArticle.title }}
                </h3>
              </a>
            } @else {
              <!-- Empty space to maintain grid layout when no next article -->
              <div></div>
            }
            @if (prevArticle) {
              <a
                [routerLink]="['/news', prevArticle.slug]"
                rel="prev"
                class="group flex flex-col space-y-2 rounded-lg border border-gray-200 bg-white p-4 transition-all hover:-translate-y-1 hover:shadow-md dark:border-gray-700 dark:bg-gray-900 sm:items-end sm:text-right"
                [class.sm:col-span-2]="!nextArticle"
                [attr.aria-label]="'Read previous article: ' + prevArticle.title"
              >
                <div class="flex items-center text-sm text-gray-500 dark:text-gray-400 sm:justify-end">
                  <span>Previous Article</span>
                  <ng-icon hlm [size]="'sm'" name="lucideArrowRight" class="ml-1.5" />
                </div>
                <h3 class="line-clamp-2 text-base font-medium text-gray-900 dark:text-gray-100">
                  {{ prevArticle.title }}
                </h3>
              </a>
            } @else {
              <!-- Empty space to maintain grid layout when no previous article -->
              <div></div>
            }
          </div>
        </nav>
      }
    </article>
  }
</div>
